/**
 * <SkuDependencyDialog />
 *
 * Shown when the admin tries to delete a Sales SKU that's still
 * referenced somewhere. Presents the reference breakdown returned
 * by `GET /api/sales/items/{id}/dependencies` (or the same payload
 * embedded in a 409 DELETE response) and offers three actions:
 *
 *   • Cancel        — close the dialog, keep the SKU
 *   • Replace SKU   — pick another rate item, remap all references,
 *                     then delete. Uses `POST /items/{id}/replace-with`.
 *   • Force Delete  — hard-delete after cleaning up non-order refs.
 *                     Disabled while any active order still points at
 *                     the SKU (only admins can trigger this at all —
 *                     the parent already gates on role).
 *
 * Props:
 *   open              — visibility flag (Dialog controls itself)
 *   onClose()         — close without deleting
 *   sourceItem        — { id, sales_sku, display_name, rate_card_id }
 *   dependencies      — payload from the /dependencies endpoint
 *   availableItems    — list of other rate items admin can pick as
 *                       replacement. Same shape as sourceItem, without
 *                       the source itself.
 *   onReplace(newId)  — async; parent calls the replace endpoint,
 *                       refreshes the list, and closes the dialog.
 *   onForceDelete()   — async; parent calls DELETE ?force=true.
 */
import { useMemo, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Loader2, AlertTriangle, Package, Users, FileText, Palette, Layers, Wrench } from 'lucide-react';

// Config for the count rows we render. `informational: true` hides the
// row from the total-references highlight and keeps it in a lighter
// footer section so it doesn't feel like a blocker.
const REF_CONFIG = [
    { key: 'bundles', label: 'Quick Order Bundles', icon: Package },
    { key: 'customer_overrides', label: 'Customer Pricing Overrides', icon: Users },
    { key: 'orders_draft', label: 'Draft Orders', icon: FileText },
    { key: 'orders_active', label: 'Active Orders (Submitted / In Production)', icon: FileText, danger: true },
    { key: 'quotes', label: 'Saved Quotes', icon: FileText, indent: true },
    { key: 'rate_card_addon_refs', label: 'Other rate items (setup fee / add-on)', icon: Layers },
    { key: 'artwork_templates', label: 'Linked Artwork Templates', icon: Palette, informational: true },
    { key: 'product_boms', label: 'Linked Product BOM', icon: Wrench, informational: true },
];

export default function SkuDependencyDialog({
    open,
    onClose,
    sourceItem,
    dependencies,
    availableItems = [],
    onReplace,
    onForceDelete,
}) {
    const [mode, setMode] = useState('summary'); // 'summary' | 'replace'
    const [replacementId, setReplacementId] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // Reset transient state whenever the dialog is re-opened for a new item.
    // (We can't rely on unmount because the parent keeps this dialog
    // mounted between opens for smoother transitions.)
    const resetLocalState = () => {
        setMode('summary');
        setReplacementId('');
        setErr(null);
        setBusy(false);
    };

    const handleClose = () => {
        resetLocalState();
        onClose?.();
    };

    const counts = dependencies?.counts || {};
    const hasActive = !!dependencies?.has_active_orders;

    const visibleRows = useMemo(() => (
        REF_CONFIG
            .map((c) => ({ ...c, count: counts[c.key] || 0 }))
            .filter((c) => c.count > 0)
    ), [counts]);

    const handleReplaceConfirm = async () => {
        if (!replacementId) return;
        setBusy(true);
        setErr(null);
        try {
            await onReplace?.(replacementId);
            resetLocalState();
        } catch (e) {
            setErr(e?.message || 'Failed to replace SKU');
        } finally {
            setBusy(false);
        }
    };

    const handleForce = async () => {
        setBusy(true);
        setErr(null);
        try {
            await onForceDelete?.();
            resetLocalState();
        } catch (e) {
            setErr(e?.message || 'Failed to force delete');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
            <DialogContent className="max-w-lg" data-testid="sku-dependency-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <DialogTitle>SKU is in use</DialogTitle>
                            <DialogDescription>
                                <span className="font-mono text-slate-700">{sourceItem?.sales_sku}</span>
                                {sourceItem?.display_name && (
                                    <span className="text-slate-500"> — {sourceItem.display_name}</span>
                                )}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                {mode === 'summary' && (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                            This SKU is currently referenced by:
                        </p>
                        <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-slate-50/40">
                            {visibleRows.length === 0 && (
                                <li className="px-4 py-3 text-sm text-slate-500">
                                    No blocking references — you can delete immediately.
                                </li>
                            )}
                            {visibleRows.map(({ key, label, icon: Icon, count, danger, informational, indent }) => (
                                <li
                                    key={key}
                                    className={`flex items-center justify-between px-4 py-2.5 text-sm ${indent ? 'pl-8' : ''}`}
                                    data-testid={`dep-row-${key}`}
                                >
                                    <span className={`flex items-center gap-2 ${informational ? 'text-slate-500' : 'text-slate-700'}`}>
                                        <Icon className={`h-4 w-4 ${danger ? 'text-red-500' : informational ? 'text-slate-400' : 'text-slate-500'}`} />
                                        {label}
                                    </span>
                                    <span className={`font-semibold ${danger ? 'text-red-600' : informational ? 'text-slate-500' : 'text-slate-900'}`}>
                                        {count}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        {hasActive && (
                            <Alert variant="destructive" className="border-red-200 bg-red-50">
                                <AlertTriangle className="h-4 w-4 text-red-600" />
                                <AlertDescription className="text-red-700 text-xs">
                                    Force Delete is disabled while active orders reference this SKU. Use
                                    <span className="font-semibold"> Replace SKU </span> to remap them or wait for the orders to complete.
                                </AlertDescription>
                            </Alert>
                        )}

                        {err && (
                            <Alert variant="destructive">
                                <AlertDescription>{err}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                disabled={busy}
                                data-testid="dep-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => setMode('replace')}
                                    disabled={busy || availableItems.length === 0}
                                    data-testid="dep-replace-btn"
                                >
                                    Replace SKU
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={handleForce}
                                    disabled={busy || hasActive}
                                    data-testid="dep-force-delete-btn"
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                    Force Delete
                                </Button>
                            </div>
                        </DialogFooter>
                    </div>
                )}

                {mode === 'replace' && (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                            Pick a Sales SKU to take over every reference. All bundles,
                            orders, invoice lines, add-ons and customer overrides will be
                            re-pointed to the replacement, then the original SKU is
                            deleted.
                        </p>
                        <div>
                            <Label htmlFor="replace-select" className="text-xs uppercase tracking-wide text-slate-500">
                                Replacement Sales SKU
                            </Label>
                            <Select value={replacementId} onValueChange={setReplacementId}>
                                <SelectTrigger id="replace-select" className="mt-1" data-testid="dep-replace-select">
                                    <SelectValue placeholder="Choose a replacement…" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {availableItems.map((it) => (
                                        <SelectItem key={it.id} value={it.id} data-testid={`dep-replace-option-${it.id}`}>
                                            <span className="font-mono">{it.sales_sku}</span>
                                            {it.display_name && (
                                                <span className="text-slate-500"> — {it.display_name}</span>
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {err && (
                            <Alert variant="destructive">
                                <AlertDescription>{err}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
                            <Button
                                variant="outline"
                                onClick={() => { setMode('summary'); setErr(null); }}
                                disabled={busy}
                            >
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose} disabled={busy}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleReplaceConfirm}
                                    disabled={busy || !replacementId}
                                    data-testid="dep-replace-confirm-btn"
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                    Replace &amp; delete
                                </Button>
                            </div>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
